import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { buildTheme } from './themes'
import Auth from './Auth'
import Dashboard from './Dashboard'
import Admin from './Admin'
import Paywall from './Paywall'

function App() {
  const [session, setSession] = useState(null)
  const [themeMode, setThemeMode] = useState('light')
  const [themeName, setThemeName] = useState('Ravenwood Classic')
  const [fontSize, setFontSize] = useState('Medium')
  const [rowDensity, setRowDensity] = useState('Comfortable')

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [trialEndsAt, setTrialEndsAt] = useState(null)
  const [isBetaUser, setIsBetaUser] = useState(false)
  const [isFounder, setIsFounder] = useState(false)
  const [stripeCustomerId, setStripeCustomerId] = useState(null)
  const [justBrowsing, setJustBrowsing] = useState(false)
  const [subLoaded, setSubLoaded] = useState(false)
  const [postCheckoutWaiting, setPostCheckoutWaiting] = useState(false)

  const theme = buildTheme(themeName, themeMode, fontSize, rowDensity)
  const toggleTheme = () => setThemeMode(m => m === 'dark' ? 'light' : 'dark')

  const applyThemeSettings = (name, mode, newFontSize, newRowDensity) => {
    if (name) setThemeName(name)
    if (mode) setThemeMode(mode)
    if (newFontSize) setFontSize(newFontSize)
    if (newRowDensity) setRowDensity(newRowDensity)
  }

  const isAdminRoute = window.location.pathname === '/admin'
  const hasSessionId = new URLSearchParams(window.location.search).has('session_id')

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!session?.user?.id) return

    if (hasSessionId) {
      // Coming back from Stripe — wait 3 seconds for the webhook to update the profile
      setPostCheckoutWaiting(true)
      const timer = setTimeout(() => {
        setPostCheckoutWaiting(false)
        const url = new URL(window.location.href)
        url.searchParams.delete('session_id')
        window.history.replaceState({}, '', url.toString())
        fetchSubscription()
      }, 3000)
      return () => clearTimeout(timer)
    }

    fetchSubscription()

    async function fetchSubscription() {
      const cols = 'subscription_status, trial_ends_at, is_beta_user, is_founder, stripe_customer_id'

      let { data } = await supabase
        .from('profiles')
        .select(cols)
        .eq('user_id', session.user.id)
        .single()

      // No profile row yet — create one now
      if (!data) {
        const trialEnd = new Date()
        trialEnd.setDate(trialEnd.getDate() + 30)
        console.log('profiles insert — session.user.email:', session.user.email)
        await supabase.from('profiles').insert({
          user_id: session.user.id,
          email: session.user.email,
          subscription_status: 'trialing',
          trial_ends_at: trialEnd.toISOString(),
        })

        // Fire Kit onboarding sequence for new users
        try {
          await fetch('/api/kit-subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: session.user.email,
              firstName: session.user.user_metadata?.full_name?.split(' ')[0] || '',
            }),
          })
        } catch (err) {
          // Non-blocking — if Kit fails, don't break the app
          console.warn('Kit subscribe failed:', err)
        }

        const { data: fresh } = await supabase
          .from('profiles')
          .select(cols)
          .eq('user_id', session.user.id)
          .single()
        data = fresh
      }

      if (data) {
        setSubscriptionStatus(data.subscription_status)
        setTrialEndsAt(data.trial_ends_at)
        setIsBetaUser(data.is_beta_user === true)
        setIsFounder(data.is_founder === true)
        setStripeCustomerId(data.stripe_customer_id || null)
      }

      setSubLoaded(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  const canEdit =
    subscriptionStatus === 'paid' ||
    subscriptionStatus === 'active' ||
    (subscriptionStatus === 'trialing' && trialEndsAt && new Date() < new Date(trialEndsAt)) ||
    isBetaUser === true

  if (isAdminRoute) {
    return (
      <div style={{ background: '#0f172a', minHeight: '100vh' }}>
        {!session ? <Auth theme={theme} /> : <Admin session={session} />}
      </div>
    )
  }

  if (!session) {
    return (
      <div style={{ background: theme.bg, minHeight: '100vh' }}>
        <Auth theme={theme} />
      </div>
    )
  }

  if (postCheckoutWaiting) {
    return (
      <div style={{
        background: theme.bg, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          fontSize: 15, color: theme.textMuted,
          fontFamily: "'Nunito Sans', sans-serif", letterSpacing: '0.05em',
        }}>
          Setting up your account...
        </div>
      </div>
    )
  }

  if (!subLoaded) {
    return <div style={{ background: theme.bg, minHeight: '100vh' }} />
  }

  if (!canEdit && !justBrowsing) {
    return (
      <Paywall
        session={session}
        subscriptionStatus={subscriptionStatus}
        onBrowse={() => setJustBrowsing(true)}
      />
    )
  }

  return (
    <div style={{ background: theme.bg, minHeight: '100vh' }}>
      <Dashboard
        session={session}
        theme={theme}
        toggleTheme={toggleTheme}
        applyThemeSettings={applyThemeSettings}
        canEdit={canEdit}
        isFounder={isFounder}
        stripeCustomerId={stripeCustomerId}
        subscriptionStatus={subscriptionStatus}
        trialEndsAt={trialEndsAt}
        isBetaUser={isBetaUser}
      />
    </div>
  )
}

export default App
